import React, {useEffect, useState} from "react";
import {
    Button,
    Card,
    Modal,
    Form,
    InputNumber,
    Select,
    Input,
    Table,
    message,
    Typography,
    DatePicker,
    Space
} from "antd";
import {DownloadOutlined} from '@ant-design/icons';
import jsPDF from "jspdf";
import logo from "../../assets/dolphin-logo.png";
import autoTable from "jspdf-autotable";
import api from "../../api";
import {Transaction} from "../../types";
import {maxW700} from "../../styles/styles";
import {exportToExcel} from "../../utils/exportToExcel";

const {RangePicker} = DatePicker;
const {Title} = Typography;
const {Option} = Select;

const nonKRWOptions = ["YEN", "USD", "EUR", "HKD", "CNY", "USDT-ERC20", "USDT-TRC20"];

const TransactionsPage: React.FC = () => {
    const [transactions, setTransactions] = useState<any[]>([]);
    const [summary, setSummary] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [groupBy, setGroupBy] = useState<string>("date");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [mmidInput, setMmidInput] = useState("");
    const [rateType, setRateType] = useState<"cc" | "walkin" | "">("");
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [ratesAvailable, setRatesAvailable] = useState<boolean>(true);
    const [summaryMeta, setSummaryMeta] = useState<{ inflow: number; outflow: number }>(
        {
            inflow: 0,
            outflow: 0,
        });
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [pagination_trans, setPagination_trans] = useState({
        current: 1,
        pageSize: 10,
    });
    const [pagination_summary, setPagination_summary] = useState({
        current: 1,
        pageSize: 10,
    });
    const [isUnlockModalVisible, setIsUnlockModalVisible] = useState(false);
    const [unlockForm] = Form.useForm();
    const [unlockingTx, setUnlockingTx] = useState<Transaction | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchTransactions(pagination_trans.current, pagination_trans.pageSize);
    }, [pagination_trans.current, pagination_trans.pageSize, dateRange]);

    useEffect(() => {
        fetchSummary(groupBy);
    }, [groupBy, dateRange]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTransactions([...transactions]);
        }, 10000); // check every second or adjust as needed

        return () => clearInterval(interval);
    }, [transactions]);

    const generatePDFReceipt = (transaction: Transaction) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        // Load logo image and draw it centered
        const desiredWidth = 30;
        const originalWidth = 450;
        const originalHeight = 637;
        const aspectRatio = originalHeight / originalWidth;
        const scaledHeight = desiredWidth * aspectRatio;
        const imgX = (pageWidth - desiredWidth) / 2;

        // Add image
        doc.addImage(logo, "PNG", imgX, 1, desiredWidth, scaledHeight);

        // Set title below the logo
        doc.setFontSize(16);
        doc.text("Dolphin Exchange Receipt", pageWidth / 2, 45, {align: "center"});

        // Add table data
        autoTable(doc, {
            startY: 55,
            body: [
                ["Transaction Type", transaction.direction],
                ["MMID", transaction.trans_mmid],
                ["Buy", transaction.buy_currency],
                ["Sell", transaction.sell_currency],
                ["Rate Type", transaction.rate_type === "cc" ? "Casino Club" : "Walk-in"],
                ["Rate", Number(transaction.rate).toFixed(2)],
                ["Amount", Number(transaction.amount).toLocaleString()],
                ["Total", Number(transaction.total).toLocaleString()],
                ["Date", new Date(transaction.timestamp).toLocaleDateString()],
            ],
            theme: "striped",
        });

        doc.save(`Transaction_${transaction.id}.pdf`);
    };

    const fetchTransactions = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            let url = `/dolphin/transactions/?page=${page}&page_size=${pageSize}`;
            if (dateRange) {
                const [start, end] = dateRange;
                url += `&start=${start}&end=${end}`;
            }

            const res = await api.get(url);

            const transactions = res.data.results || res.data;
            setTransactions(transactions);
            setTotalTransactions(res.data.count || 0);
        } catch (err) {
            message.error("Failed to load transactions");
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async (grouping: string) => {
        setLoading(true)
        try {
            let url = `/dolphin/transactions/summary/?group_by=${grouping}`;
            if (dateRange) {
                const [start, end] = dateRange;
                url += `&start=${start}&end=${end}`;
            }

            const res = await api.get(url);
            setSummary(res.data.data);
            setSummaryMeta({inflow: res.data.inflow, outflow: res.data.outflow});
        } catch (err) {
            message.error("Failed to load summary");
        } finally {
            setLoading(false)
        }
    };

    const fetchDynamicRate = async (explicitRateType?: string) => {
        const {direction, currency} = form.getFieldsValue();
        if (!direction || !currency) return;

        const buy = direction === "Buy" ? currency : "KRW";
        const sell = direction === "Buy" ? "KRW" : currency;
        const rateToUse = explicitRateType || rateType;

        try {
            const res = await api.get(`/dolphin/rates/convert/?buy=${buy}&sell=${sell}&rate_type=${rateToUse}`);
            const rate = res.data.rate;

            if (!rate || rate <= 0) {
                setRatesAvailable(false);
                message.error("Rates not set. Please configure exchange rates first.");
                return;
            }

            setRatesAvailable(true);
            form.setFieldsValue({rate});

            const amount = form.getFieldValue("amount");
            if (amount) {
                const total = +(rate * amount).toFixed(2);
                form.setFieldsValue({total});
            }
        } catch {
            setRatesAvailable(false);
            message.error("Failed to fetch rate. Please configure exchange rates.");
            form.setFieldsValue({rate: null, total: null});
        }
    };

    const searchCustomerByMMID = async () => {
        if (!mmidInput) return;

        try {
            const res = await api.get(`/cc/customers/by_mmid/?mmid=${mmidInput}`);

            const exactMatch = res.data.find((cust: any) => cust.mmid === mmidInput);
            const rateTypeResolved = exactMatch ? "cc" : "walkin";

            // Ensure both values are set correctly and consistently
            form.setFieldsValue({
                trans_mmid: mmidInput,
                rate_type: rateTypeResolved,
            });

            setRateType(rateTypeResolved);
            fetchDynamicRate(rateTypeResolved);

            message.success(
                exactMatch
                    ? `MMID found. Using CC rate.`
                    : `MMID not found. Defaulting to Walk-in rate.`
            );
        } catch (err) {
            console.error("Customer lookup failed", err);
            message.error("Customer lookup failed");
        }
    };

    const openUnlockModal = (originalTx: Transaction) => {
        const reversedDirection = originalTx.direction === "Buy" ? "Sell" : "Buy";
        const currency = originalTx.direction === "Buy"
            ? originalTx.buy_currency
            : originalTx.sell_currency;

        setUnlockingTx(originalTx);
        unlockForm.setFieldsValue({
            direction: reversedDirection,
            currency,
            rate: originalTx.rate,
            original_transaction: originalTx.id,
        });
        setIsUnlockModalVisible(true);
    };


    const calculateTotal = () => {
        const {rate, amount} = form.getFieldsValue();
        if (rate && amount) {
            const total = +(rate * amount).toFixed(2);
            form.setFieldsValue({total});
        }
    };

    const handleValuesChange = (changedValues: any, allValues: any) => {
        if (
            Object.prototype.hasOwnProperty.call(changedValues, "direction") ||
            Object.prototype.hasOwnProperty.call(changedValues, "currency")
        ) {
            fetchDynamicRate();
        } else if (Object.prototype.hasOwnProperty.call(changedValues, "amount")) {
            calculateTotal();
        }
    };

    const handleCreate = async (values: any) => {
        const isBuying = values.direction === "Buy";

        const buy_currency = isBuying ? values.currency : "KRW";
        const sell_currency = isBuying ? "KRW" : values.currency;
        const direction = isBuying ? "Buy" : "Sell";

        const payload = {
            ...values,
            lock_rate: values.lock_rate || false,
            rate_type: values.rate_type ?? rateType,
            buy_currency,
            sell_currency,
            direction,
        };

        try {
            await api.post("/dolphin/transactions/", payload);
            message.success("Transaction recorded");
            setIsModalVisible(false);
            form.resetFields();
            setMmidInput("");
            setRateType("");
            fetchTransactions();
            fetchSummary(groupBy);
        } catch (err) {
            message.error("Failed to save transaction");
        }
    };

    return (
        <Card
            title={<Title level={2}>Dolphin Transactions</Title>}
            extra={<Button
                type="primary"
                onClick={() => {
                    if (!ratesAvailable) {
                        message.warning("You must configure exchange rates before creating a transaction.");
                        return;
                    }
                    setIsModalVisible(true);
                }}
            >
                New Transaction
            </Button>}
        >
            <Space style={{marginBottom: 16}}>
                <RangePicker
                    allowClear
                    onChange={(dates, dateStrings) => {
                        if (dates && dateStrings[0] && dateStrings[1]) {
                            setDateRange([dateStrings[0], dateStrings[1]]);
                        } else {
                            setDateRange(null);
                        }
                    }}
                />
                <Space style={{marginLeft: 16}}>
                    <Typography.Paragraph>
                        <b>Total Inflow:</b> ₩{Number(summaryMeta.inflow).toLocaleString()}<br/>
                        <b>Total Outflow:</b> ₩{Number(summaryMeta.outflow).toLocaleString()}
                    </Typography.Paragraph>
                </Space>
            </Space>


            <Table
                rowKey="id"
                loading={loading}
                dataSource={transactions}
                pagination={{
                    current: pagination_trans.current,
                    pageSize: pagination_trans.pageSize,
                    total: totalTransactions,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination_trans({current: page, pageSize});
                    },
                }}
                columns={[
                    {title: "MMID", dataIndex: "trans_mmid"},
                    {
                        title: "Rate Type",
                        dataIndex: "rate_type",
                        render: (val: string) => (val === "cc" ? "Casino Club" : val === "walkin" ? "Walk-in" : val)
                    },
                    {
                        title: "Lock Rate",
                        dataIndex: "lock_rate",
                        render: (_: any, record: any) => {
                            const createdTime = new Date(record.timestamp).getTime();
                            const now = Date.now();
                            const within15Sec = now - createdTime <= 10000; // For Testing
                            const within72hrs = now - createdTime <= 72 * 60 * 60 * 1000; // For Prod

                            if (record.is_unlock_reversal) {
                                return "Unlocked";
                            }

                            if (record.was_unlocked) {
                                return "Yes (cleared)";
                            }

                            if (record.lock_rate) {
                                if (within72hrs) {
                                    return (
                                        <Button size="small" type="link" onClick={() => openUnlockModal(record)}>
                                            Unlock
                                        </Button>
                                    );
                                }
                                return "Yes (expired)";
                            }

                            return "No";
                        },
                    },
                    {title: "Buy", dataIndex: "buy_currency"},
                    {title: "Sell", dataIndex: "sell_currency"},
                    {
                        title: "Rate",
                        dataIndex: "rate",
                        render: (rate: any) => {
                            const num = Number(rate);
                            return !isNaN(num) ? num.toFixed(2) : "-";
                        },
                    },
                    {
                        title: "Amount",
                        dataIndex: "amount",
                        render: (amount: any) => {
                            const num = Number(amount);
                            return !isNaN(num) ? num.toLocaleString(undefined, {maximumFractionDigits: 0}) : "-";
                        },
                    },
                    {
                        title: "Total",
                        dataIndex: "total",
                        render: (total: any) => {
                            const num = Number(total);
                            return !isNaN(num) ? num.toLocaleString(undefined, {maximumFractionDigits: 0}) : "-";
                        },
                    },
                    {
                        title: "Timestamp",
                        dataIndex: "timestamp",
                        render: (timestamp: string) =>
                            new Date(timestamp).toLocaleString("en-US", {
                                timeZone: "Asia/Seoul",
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                            }),
                    },
                    {
                        title: "Receipt",
                        render: (_: any, record: any) => {
                            const total = Number(record.total);
                            if (isNaN(total) || total > 3000000) return null;
                            return (
                                <Button size="small" onClick={() => generatePDFReceipt(record)}>
                                    Export PDF
                                </Button>
                            );
                        }
                    },
                ]}
                scroll={{x: "max-content"}}
                onRow={(record) => {
                    const transactionDate = new Date(record.timestamp).setHours(0, 0, 0, 0);
                    const today = new Date().setHours(0, 0, 0, 0);
                    const isPast = today > transactionDate;

                    return {
                        style: {
                            backgroundColor: isPast ? "#f0f0f0" : undefined,
                            color: isPast ? "#888" : undefined,
                        },
                    };
                }}
            />
            <Space style={{marginBottom: 16}}>
                <Select value={groupBy} onChange={setGroupBy} style={{width: 160}}>
                    <Option value="date">Daily</Option>
                    <Option value="month">Monthly</Option>
                    <Option value="currency">By Currency</Option>
                </Select>
                <Button
                    icon={<DownloadOutlined/>}
                    type="primary"
                    onClick={() => {
                        const exportLabel =
                            groupBy === "currency" ? "Currency" :
                                groupBy === "month" ? "Month" :
                                    "Date";

                        const formatted = summary.map(({key, total_krw}) => {
                            let displayKey = key;

                            if (groupBy === "month") {
                                const date = new Date(key);
                                displayKey = date.toLocaleString("en-US", {month: "long", year: "numeric"}); // ➜ "April 2025"
                            }

                            return {
                                [exportLabel]: displayKey,
                                "Total (₩)": total_krw.toLocaleString(),
                            };
                        });

                        exportToExcel(formatted, `summary_${groupBy}.xlsx`, "Summary");
                    }}
                >
                    Export to Excel
                </Button>
            </Space>
            <Title level={4} style={{marginTop: 32}}>Summary by {groupBy}</Title>
            <Table
                rowKey="key"
                loading={loading}
                style={maxW700}
                dataSource={[...summary].reverse()}
                pagination={{
                    ...pagination_summary,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination_summary({current: page, pageSize});
                    },
                }}
                columns={[
                    {
                        title: groupBy === "currency" ? "Currency" : groupBy === "month" ? "Month" : "Date",
                        dataIndex: "key",
                        render: (key: string) => {
                            if (groupBy === "month") {
                                const date = new Date(key);
                                return date.toLocaleString("en-US", {month: "long", year: "numeric"}); // e.g., "March 2025"
                            }
                            return key; // fallback for date or currency
                        },
                    },
                    {
                        title: "Total (₩)",
                        dataIndex: "total_krw",
                        render: (val) => Number(val).toLocaleString(),
                    },
                ]}
                scroll={{x: "max-content"}}
            />


            <Modal
                title="New Transaction"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setMmidInput("");
                    setRateType("")
                }}
                onOk={() => form.submit()}
                okText="Save"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreate}
                    onValuesChange={handleValuesChange}
                >

                    <Form.Item
                        label="Customer MMID"
                        validateStatus={
                            mmidInput === "1" || /^\d{8}$/.test(mmidInput) || mmidInput === ""
                                ? undefined
                                : "error"
                        }
                        help={
                            mmidInput && mmidInput !== "1" && !/^\d{8}$/.test(mmidInput)
                                ? "MMID must be exactly 8 digits or the number 1"
                                : undefined
                        }
                    >
                        <Space>
                            <Input
                                placeholder="Enter MMID"
                                value={mmidInput}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d*$/.test(value)) {
                                        setMmidInput(value);
                                    }
                                }}
                                maxLength={8}
                                style={{width: 200}}
                            />
                            <Button
                                onClick={searchCustomerByMMID}
                                disabled={
                                    !(mmidInput === "1" || /^\d{8}$/.test(mmidInput))
                                }
                            >
                                Search
                            </Button>
                        </Space>
                    </Form.Item>

                    <Form.Item name="lock_rate" label="Lock Rate?" initialValue={false}>
                        <Select>
                            <Option value={false}>No</Option>
                            <Option value={true}>Yes</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="rate_type" style={{display: "none"}}>
                        <Input type="hidden"/>
                    </Form.Item>

                    <Form.Item label="Rate Type" rules={[{required: true}]}>
                        <Input
                            value={
                                rateType === "cc"
                                    ? "CC"
                                    : rateType === "walkin"
                                        ? "Walk-in"
                                        : ""
                            }
                            disabled
                        />
                    </Form.Item>

                    <Form.Item name="trans_mmid" style={{display: "none"}}>
                        <Input type="hidden"/>
                    </Form.Item>

                    <Form.Item name="direction" label="Transaction Type" rules={[{required: true}]}>
                        <Select placeholder="Select type">
                            <Option value="Buy">Buy</Option>
                            <Option value="Sell">Sell</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="currency" label="Currency" rules={[{required: true}]}>
                        <Select placeholder="Select currency">
                            {nonKRWOptions.map((cur) => (
                                <Option key={cur} value={cur}>{cur}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="rate" label="Rate">
                        <InputNumber style={{width: "100%"}} disabled/>
                    </Form.Item>

                    <Form.Item name="amount" label="Amount" rules={[{required: true}]}>
                        <InputNumber
                            style={{width: "100%"}}
                            min={0}
                            formatter={(value: string | number | undefined) =>
                                typeof value === "number"
                                    ? value.toLocaleString()
                                    : typeof value === "string"
                                        ? value.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                        : ""
                            }
                            parser={(value: string | undefined): number => {
                                return value ? parseFloat(value.replace(/,/g, "")) : 0;
                            }}
                        />
                    </Form.Item>

                    <Form.Item name="total" label="Total">
                        <InputNumber
                            style={{width: "100%"}}
                            min={0}
                            disabled
                            formatter={(value: string | number | undefined) =>
                                typeof value === "number"
                                    ? value.toLocaleString()
                                    : typeof value === "string"
                                        ? value.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                        : ""
                            }
                            parser={(value: string | undefined): number => {
                                return value ? parseFloat(value.replace(/,/g, "")) : 0;
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Unlock Transaction"
                open={isUnlockModalVisible}
                onCancel={() => {
                    setIsUnlockModalVisible(false);
                    unlockForm.resetFields();
                    setUnlockingTx(null);
                }}
                onOk={() => unlockForm.submit()}
                okText="Confirm Reversal"
            >
                <Form
                    form={unlockForm}
                    layout="vertical"
                    onFinish={async (values) => {
                        if (!unlockingTx) return;

                        const isBuying = values.direction === "Buy";
                        const payload = {
                            ...values,
                            rate_type: unlockingTx.rate_type,
                            trans_mmid: unlockingTx.trans_mmid,
                            buy_currency: isBuying ? values.currency : "KRW",
                            sell_currency: isBuying ? "KRW" : values.currency,
                            total: +(values.rate * values.amount).toFixed(0),
                            is_unlock_reversal: true,
                            original_transaction: unlockingTx.id,
                        };

                        try {
                            await api.post("/dolphin/transactions/", payload);
                            message.success("Unlock transaction recorded");
                            setIsUnlockModalVisible(false);
                            unlockForm.resetFields();
                            setUnlockingTx(null);
                            fetchTransactions();
                            fetchSummary(groupBy);
                        } catch (err) {
                            console.error(err);
                            message.error("Failed to record reversal");
                        }
                    }}
                    onValuesChange={(_, allValues) => {
                        const {rate, amount} = allValues;
                        if (rate && amount) {
                            unlockForm.setFieldsValue({
                                total: +(rate * amount).toFixed(0)
                            });
                        }
                    }}
                >
                    <Form.Item label="Direction" name="direction">
                        <Input disabled/>
                    </Form.Item>

                    <Form.Item label="Currency" name="currency">
                        <Input disabled/>
                    </Form.Item>

                    <Form.Item label="Rate" name="rate">
                        <InputNumber style={{width: "100%"}} disabled/>
                    </Form.Item>

                    <Form.Item
                        label={`Amount (max: ${unlockingTx?.amount.toLocaleString(undefined, {maximumFractionDigits: 0})})`}
                        name="amount"
                        rules={[
                            {required: true},
                            {
                                validator(_, value) {
                                    if (!unlockingTx) return Promise.resolve();
                                    if (value <= unlockingTx.amount) return Promise.resolve();
                                    return Promise.reject(new Error("Amount exceeds original transaction"));
                                },
                            },
                        ]}
                    >
                        <InputNumber
                            style={{width: "100%"}}
                            min={0}
                            precision={0}
                            formatter={(value: string | number | undefined) =>
                                typeof value === "number" || typeof value === "string"
                                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                    : ""
                            }
                            parser={(value: string | undefined) =>
                                parseInt(value?.replace(/,/g, "") || "0", 10)
                            }
                        />
                    </Form.Item>
                    <Form.Item name="total" label="Total">
                        <InputNumber
                            style={{width: "100%"}}
                            disabled
                            formatter={(value) =>
                                value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                            }
                            parser={(value) => parseInt(value?.replace(/,/g, "") || "0", 10)}
                        />
                    </Form.Item>

                    <Form.Item name="original_transaction" style={{display: "none"}}>
                        <Input type="hidden"/>
                    </Form.Item>

                    <Form.Item name="rate_type" style={{display: "none"}}>
                        <Input type="hidden"/>
                    </Form.Item>

                    <Form.Item name="trans_mmid" style={{display: "none"}}>
                        <Input type="hidden"/>
                    </Form.Item>
                </Form>
            </Modal>

        </Card>
    );
};

export default TransactionsPage;